import {createShapeId} from 'tldraw';
import {getEntityKBUrl, getWikidataUrl, isLocalEntityRef} from '../../../../utils/entityLinks';
import {resolveMutationEntityLabel} from '../../ai/mutationEntityLabel';
import {
    computeEntityCardHeight,
    ENTITY_CARD_HEIGHT,
    ENTITY_CARD_SHAPE_TYPE,
    ENTITY_CARD_WIDTH,
} from './entityCardConstants';

export {
    pinAlias,
    pinKbLastUpdated,
    pinPropertySnapshot,
    refreshPinnedAliases,
    refreshPinnedPropertySnapshots,
    unpinAlias,
    unpinKbLastUpdated,
    unpinPropertySnapshot,
} from './entityCardPinUtils';

/** Optional numeric props — tldraw rejects null for T.optional(T.number). */
const OPTIONAL_NUMBER_PROP_KEYS = ['carverTotal', 'carverScoredFactorCount'];
/** Optional boolean props — omit rather than send null/undefined. */
const OPTIONAL_BOOLEAN_PROP_KEYS = ['carverPinned', 'kbLastUpdatedPinned'];
const STRING_PROP_KEYS = [
    'wikidataId',
    'label',
    'description',
    'imageUrl',
    'kbUrl',
    'wikidataUrl',
    'kbLastUpdatedAt',
];

function normalizeCountries(countries) {
    return [...new Set((countries || []).filter(Boolean))].slice(0, 10);
}

function normalizeRelatedCountryDetails(details, fallbackNames = []) {
    if (Array.isArray(details) && details.length > 0) {
        const deduped = new Map();
        for (const item of details) {
            const name = item?.name?.trim();
            if (!name) continue;
            const existing = deduped.get(name);
            const flagUrl = item?.flagUrl || existing?.flagUrl || '';
            deduped.set(name, {name, flagUrl});
        }
        return Array.from(deduped.values()).slice(0, 10);
    }

    return normalizeCountries(fallbackNames).map((name) => ({name, flagUrl: ''}));
}

/**
 * Coerce entity-card image URLs for sync. Prefer relative `/api/...` or https URLs;
 * drop blob:/data: payloads that can blow past sync validators or bloat snapshots.
 */
export function normalizeEntityCardImageUrl(raw) {
    if (raw == null) return '';
    const value = typeof raw === 'string' ? raw.trim() : String(raw);
    if (!value) return '';
    if (value.startsWith('blob:') || value.startsWith('data:')) return '';
    return value;
}

/**
 * Sanitize a partial props patch before editor.updateShapes.
 * Drops null/undefined, coerces numbers/booleans/strings so sync schema validation passes.
 *
 * @param {Record<string, unknown>|null|undefined} partialProps
 * @returns {Record<string, unknown>}
 */
export function sanitizeEntityCardPropPatch(partialProps) {
    if (!partialProps || typeof partialProps !== 'object') return {};

    const props = {};
    for (const [key, value] of Object.entries(partialProps)) {
        if (value === null || value === undefined) continue;

        if (OPTIONAL_NUMBER_PROP_KEYS.includes(key)) {
            props[key] = Number.isFinite(value) ? value : 0;
            continue;
        }
        if (OPTIONAL_BOOLEAN_PROP_KEYS.includes(key)) {
            props[key] = Boolean(value);
            continue;
        }
        if (key === 'imageUrl' || STRING_PROP_KEYS.includes(key)) {
            props[key] = key === 'imageUrl'
                ? normalizeEntityCardImageUrl(value)
                : (typeof value === 'string' ? value : String(value));
            continue;
        }
        if (key === 'pinnedProperties') {
            props[key] = Array.isArray(value) ? value : [];
            continue;
        }
        if (key === 'pinnedAliases') {
            props[key] = Array.isArray(value)
                ? value.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim())
                : [];
            continue;
        }
        if (key === 'relatedCountries') {
            props[key] = Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
            continue;
        }
        if (key === 'relatedCountryDetails') {
            props[key] = Array.isArray(value) ? value : [];
            continue;
        }
        if (key === 'w' || key === 'h') {
            props[key] = Number.isFinite(value) ? value : 0;
            continue;
        }
        props[key] = value;
    }
    return props;
}

export function buildEntityCardShapeProps(entity) {
    const wikidataId = entity?.id || entity?.wikidataId || '';
    // Never let a bare QID win when plan / Wikidata / search provided a human name.
    const label = resolveMutationEntityLabel({
        ...entity,
        id: wikidataId,
        wikidataId,
    });
    const description = entity?.description || '';
    const imageUrl = normalizeEntityCardImageUrl(entity?.imageUrl || entity?.image || '');
    const isLocal = isLocalEntityRef(wikidataId);

    const relatedCountries = normalizeCountries(entity?.relatedCountries);
    const relatedCountryDetails = normalizeRelatedCountryDetails(entity?.relatedCountryDetails, relatedCountries);
    const kbUrl = wikidataId ? getEntityKBUrl(wikidataId) : '';
    const pinnedProperties = Array.isArray(entity?.pinnedProperties) ? entity.pinnedProperties : [];
    const pinnedAliases = Array.isArray(entity?.pinnedAliases) ? entity.pinnedAliases : [];
    const carverPinned = Boolean(entity?.carverPinned);
    const carverTotal = Number.isFinite(entity?.carverTotal) ? entity.carverTotal : 0;
    const carverScoredFactorCount = Number.isFinite(entity?.carverScoredFactorCount)
        ? entity.carverScoredFactorCount
        : 0;
    const kbLastUpdatedPinned = Boolean(entity?.kbLastUpdatedPinned);
    const kbLastUpdatedAt = typeof entity?.kbLastUpdatedAt === 'string' ? entity.kbLastUpdatedAt : '';

    // Always emit defined, schema-valid values (including optional CARVER fields).
    // Omitting them on create is fine for T.optional, but defaults keep client/server records aligned.
    return sanitizeEntityCardPropPatch({
        w: ENTITY_CARD_WIDTH,
        h: computeEntityCardHeight({
            description,
            relatedCountries,
            relatedCountryDetails,
            kbUrl,
            pinnedProperties,
            pinnedAliases,
            carverPinned,
            kbLastUpdatedPinned,
        }),
        wikidataId,
        label,
        description,
        imageUrl,
        kbUrl,
        wikidataUrl: isLocal ? '' : (wikidataId ? (getWikidataUrl(wikidataId) || '') : ''),
        relatedCountries,
        relatedCountryDetails,
        pinnedProperties,
        pinnedAliases,
        carverTotal,
        carverScoredFactorCount,
        carverPinned,
        kbLastUpdatedPinned,
        kbLastUpdatedAt,
    });
}

export function createEntityCardShape({entity, x, y, id = createShapeId()}) {
    return {
        id,
        type: ENTITY_CARD_SHAPE_TYPE,
        x,
        y,
        props: buildEntityCardShapeProps(entity),
    };
}

export {parseEntityCardDragPayload, serializeEntityCardDragPayload} from './entityCardDrag';

export function isEntityCardShape(shape) {
    return shape?.type === ENTITY_CARD_SHAPE_TYPE;
}

export function getCenteredEntityCardPosition(pagePoint, cardHeight = ENTITY_CARD_HEIGHT) {
    return {
        x: pagePoint.x - ENTITY_CARD_WIDTH / 2,
        y: pagePoint.y - cardHeight / 2,
    };
}
